import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Globe, Phone, Facebook, Instagram, MessageSquare, Sparkles, Calendar as CalendarIcon, DollarSign, Car, Clock, Users, Image as ImageIcon } from 'lucide-react';
import { fairApi, slotApi } from '../services/api';
import { Fair, SlotAvailabilitySummary, VendorPublicProfile, FairVendorResponse } from '../types';
import SEO from './SEO';
import FavoriteButton from './FavoriteButton';
import ReviewList from './ReviewList';
import FairQRCode from './FairQRCode';
import { SlotSelectionModal } from './SlotSelectionModal';
import FairInquiryModal from './FairInquiryModal';
import SmartBoothSelector from './SmartBoothSelector';
import InteractiveBoothMapWrapper from './InteractiveBoothMapWrapper';
import VendorCarousel from './VendorCarousel';
import FairGallery from './FairGallery';
import GetDirections from './GetDirections';
import AddToCalendar from './AddToCalendar';
import SocialShare from './SocialShare';
import ApplyNowFlow from './ApplyNowFlow'; // Issue #1453: Apply Now flow for seeded fairs
import { VendorContext } from '../contexts/VendorContext';
// Issue #860: Use RBAC utility functions
import { isAdmin } from '../utils/rbac';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateOnly } from '../utils/fairStatus'; // Issue #1672: Timezone-safe date display
import { useCurrentUser } from '../hooks/useCurrentUser'; // Issue #1149: Centralized user access
import SeekingManagerBadge from './SeekingManagerBadge'; // Issue #1451: Seeking manager badge for seeded fairs
import { useFeatureFlag } from '../hooks/useFeatureFlag'; // Issue #1507: Feature flag for customer reviews

export default function FairDetails() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const { id: currentUserId, isAuthenticated } = useCurrentUser(); // Issue #1149: Use centralized user hook
  const [fair, setFair] = useState<Fair | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Issue #860: Use RBAC state for admin check
  const [adminStatus, setAdminStatus] = useState(false);
  const [slotAvailability, setSlotAvailability] = useState<SlotAvailabilitySummary | null>(null);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [vendors, setVendors] = useState<VendorPublicProfile[]>([]);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showSmartRecommendations, setShowSmartRecommendations] = useState(false);
  const [showApplyNowModal, setShowApplyNowModal] = useState(false); // Issue #1453: Apply Now modal state

  // Get user's vendor profiles from context (Issue #377)
  const vendorContext = useContext(VendorContext);
  const userVendors = vendorContext?.vendors || [];

  // Issue #1452: Check if fair is seeded (no manager assigned yet)
  const isSeededFair = fair && fair.has_managers === false;

  // Issue #1507: Feature flag for customer reviews on fair details
  const { isEnabled: showCustomerReviews } = useFeatureFlag('customer_reviews_on_fair_details');

  // Update admin status when user authentication changes (Issue #860, Issue #1149)
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (isAuthenticated && token) {
      // Issue #860: Use RBAC utility function for admin check
      setAdminStatus(isAdmin());
    } else {
      setAdminStatus(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadFair = async () => {
      if (!fairId) return;

      try {
        setLoading(true);
        const data = await fairApi.getById(Number(fairId));
        setFair(data);
        setError(null);
      } catch (err: unknown) {
        console.error('Failed to load artist fair:', err);
        setError('Fair not found');
        setFair(null);
      } finally {
        setLoading(false);
      }
    };

    loadFair();
  }, [fairId]);

  // Fetch slot availability summary (Issue #348)
  useEffect(() => {
    if (!fair?.id) return;

    const abortController = new AbortController();

    const fetchSlotAvailability = async () => {
      try {
        const summary = await slotApi.getSlotAvailabilitySummary(fair.id);
        if (!abortController.signal.aborted) {
          setSlotAvailability(summary);
        }
      } catch (err: unknown) {
        if (!abortController.signal.aborted) {
          console.error('Failed to load slot availability:', err);
          setSlotAvailability(null);
        }
      }
    };

    fetchSlotAvailability();

    return () => {
      abortController.abort();
    };
  }, [fair?.id]);

  // Fetch vendors at this fair (Issue #353)
  useEffect(() => {
    if (!fair?.id) return;

    const abortController = new AbortController();

    const fetchVendors = async () => {
      try {
        const vendorData = await fairApi.getFairVendors(fair.id);
        if (!abortController.signal.aborted) {
          // Map FairVendorResponse to VendorPublicProfile (vendor_id -> id)
          const mappedVendors = vendorData.map((v: FairVendorResponse) => ({
            id: v.vendor_id,
            business_name: v.business_name,
            description: v.description,
            website: v.website,
            product_types: v.product_types,
            portfolio_images: v.portfolio_images,
            is_active: true,
          }));
          setVendors(mappedVendors);
        }
      } catch (err: unknown) {
        if (!abortController.signal.aborted) {
          console.error('Failed to load vendors:', err);
          setVendors([]);
        }
      }
    };

    fetchVendors();

    return () => {
      abortController.abort();
    };
  }, [fair?.id]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading artist fair...</p>
      </div>
    );
  }

  if (error || !fair) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error || 'Fair not found'}</p>
      </div>
    );
  }

  // Generate dynamic SEO data for the artist fair
  const generateSEOTitle = () => {
    return `${fair.name} - Local Artisan Fair in ${fair.address.city}, ${fair.address.state}`;
  };

  const generateSEODescription = () => {
    const vendorCount = vendors.length;
    const vendorText = vendorCount > 0 ? `featuring ${vendorCount} local vendor${vendorCount !== 1 ? 's' : ''}` : 'with local vendors';
    return `Visit ${fair.name} in ${fair.address.city}, ${fair.address.state} ${vendorText}. ${fair.description || 'Fresh, local artisan products available.'} Find contact info, hours, and more.`;
  };

  const generateSEOKeywords = () => {
    const baseKeywords = `${fair.name}, artist fair, local artisans, craft fair, ${fair.address.city}, ${fair.address.state}`;
    const vendorKeywords = vendors.map(v => v.business_name).join(', ');
    return vendorKeywords ? `${baseKeywords}, ${vendorKeywords}` : baseKeywords;
  };

  // Create structured data for the artist fair
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `https://localartistfairs.com/fairs/${fair.id}`,
    "name": fair.name,
    "description": fair.description || `Local artisan fair at ${fair.name}`,
    "url": `https://localartistfairs.com/fairs/${fair.id}`,
    "image": fair.image,
    "startDate": fair.start_date,
    "endDate": fair.end_date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": fair.name,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": fair.address.street,
        "addressLocality": fair.address.city,
        "addressRegion": fair.address.state,
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": fair.address.coordinates.lat,
        "longitude": fair.address.coordinates.lng
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": fair.name,
      "url": fair.website
    },
    "offers": fair.attendee_entry_fee ? {
      "@type": "Offer",
      "price": fair.attendee_entry_fee,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    } : undefined
  };

  return (
    <>
      <SEO
        title={generateSEOTitle()}
        description={generateSEODescription()}
        keywords={generateSEOKeywords()}
        url={`https://localartistfairs.com/fairs/${fair.id}`}
        image={fair.image}
        type="article"
        structuredData={structuredData}
      />

      {/* Issue #1199: Enhanced Customer View - Hero Section */}
      <div className="bg-gradient-to-br from-teal-50 to-moonbone -mx-4 px-4 py-8 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Hero Image */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img
                  src={fair.image}
                  alt={fair.name}
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>

            {/* Key Information Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 space-y-4 h-fit">
              <div className="flex items-start justify-between gap-2">
                <h1 className="text-3xl font-bold text-primary-dark flex-1">{fair.name}</h1>
                <FavoriteButton fairId={fair.id} />
              </div>

              {/* Seeking Manager Badge (Issue #1451) */}
              {fair.submitted_by == null && (
                <div>
                  <SeekingManagerBadge size="medium" />
                </div>
              )}

              {/* Issue #1199: Prominent Fair Info */}
              <div className="space-y-3">
                {/* Date & Time */}
                {fair.start_date && fair.end_date && (
                  <div className="flex items-start gap-3">
                    <CalendarIcon className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">
                        {formatDateOnly(fair.start_date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        {fair.start_date !== fair.end_date && (
                          <> - {formatDateOnly(fair.end_date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</>
                        )}
                      </div>
                      {fair.hours && (
                        <div className="text-gray-600 flex items-center gap-1 mt-1">
                          <Clock className="h-3.5 w-3.5" />
                          {fair.hours}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Location */}
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{fair.address.street}</div>
                    <div className="text-gray-600">{fair.address.city}, {fair.address.state}</div>
                  </div>
                </div>

                {/* Admission Fee */}
                {fair.attendee_entry_fee && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">Admission</div>
                      <div className="text-gray-600">{formatCurrency(fair.attendee_entry_fee)}</div>
                    </div>
                  </div>
                )}

                {/* Parking Info */}
                {fair.parking_info && (
                  <div className="flex items-start gap-3">
                    <Car className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">Parking</div>
                      <div className="text-gray-600">{fair.parking_info}</div>
                    </div>
                  </div>
                )}

                {/* Expected Attendance */}
                {fair.expected_attendance && (
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">Expected Attendance</div>
                      <div className="text-gray-600">{fair.expected_attendance.toLocaleString()} visitors</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons - Issue #1199 */}
              <div className="pt-4 border-t border-gray-200 space-y-2">
                {fair.start_date && fair.end_date && (
                  <AddToCalendar
                    eventName={fair.name}
                    startDate={fair.start_date}
                    endDate={fair.end_date}
                    location={`${fair.address.street}, ${fair.address.city}, ${fair.address.state}`}
                    description={fair.description}
                    hours={fair.hours}
                  />
                )}
                <GetDirections
                  address={fair.address}
                  fairName={fair.name}
                />
                <SocialShare
                  title={fair.name}
                  url={`https://localartistfairs.com/fairs/${fair.id}`}
                  description={fair.description}
                />
              </div>

              {/* Social Links */}
              {(fair.website || fair.phone || fair.social?.facebook || fair.social?.instagram || fair.social?.twitter) && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="space-y-2 text-sm">
                    {fair.website && (
                      <a href={fair.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-700 hover:text-secondary transition-colors">
                        <Globe className="h-4 w-4" />
                        <span>Visit Website</span>
                      </a>
                    )}
                    {fair.phone && (
                      <a href={`tel:${fair.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-secondary transition-colors">
                        <Phone className="h-4 w-4" />
                        <span>{fair.phone}</span>
                      </a>
                    )}
                  </div>
                  {(fair.social?.facebook || fair.social?.instagram) && (
                    <div className="flex gap-3 mt-3">
                      {fair.social?.facebook && (
                        <a href={`https://facebook.com/${fair.social.facebook}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary transition-colors">
                          <Facebook className="h-5 w-5" />
                        </a>
                      )}
                      {fair.social?.instagram && (
                        <a href={`https://instagram.com/${fair.social.instagram}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-600 transition-colors">
                          <Instagram className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* QR Code - Admin/Manager View */}
              {adminStatus && (
                <div className="pt-4 border-t border-gray-200">
                  <FairQRCode
                    fairId={fair.id}
                    fairName={fair.name}
                    allowDownload={adminStatus}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        {/* Description Section */}
        {fair.description && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-primary-dark mb-4">About This Fair</h2>
            <p className="text-gray-700 text-lg leading-relaxed">{fair.description}</p>
          </div>
        )}

        {/* Issue #1199: Fair Gallery */}
        {fair.images && fair.images.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-primary-dark mb-2 flex items-center gap-2">
              <ImageIcon className="h-6 w-6" />
              <span>Fair Gallery</span>
            </h2>
            <p className="text-gray-600 mb-6">Photos from previous years and highlights</p>
            <FairGallery images={fair.images} fairName={fair.name} />
          </div>
        )}

        {/* Issue #1199: Vendor Preview Carousel */}
        {vendors.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <VendorCarousel
              vendors={vendors}
              title="Featured Vendors at This Fair"
              autoRotate={true}
              autoRotateInterval={6000}
              itemsPerPage={3}
            />
          </div>
        )}

        {/* Fair Amenities & Features (Issue #54) */}
        {(fair.has_live_music || fair.has_alcohol || fair.has_guest_appearances ||
          fair.has_giveaways || fair.has_food_trucks || fair.is_affiliated || fair.is_indoor !== undefined) && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-primary-dark mb-4">Amenities & Features</h2>
            <div className="flex flex-wrap gap-3">
              {fair.has_live_music && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  🎵 Live Music
                </span>
              )}
              {fair.has_alcohol && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                  🍺 Beer & Wine
                </span>
              )}
              {fair.has_guest_appearances && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-pink-100 text-pink-800">
                  ⭐ Guest Appearances
                </span>
              )}
              {fair.has_giveaways && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  🎁 Giveaways
                </span>
              )}
              {fair.has_food_trucks && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                  🚚 Food Trucks
                </span>
              )}
              {fair.is_affiliated && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-moonbone text-ink">
                  🤝 Affiliated Event
                </span>
              )}
              {fair.is_indoor !== undefined && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {fair.is_indoor ? '🏢 Indoor Venue' : '🌳 Outdoor Venue'}
                </span>
              )}
              {fair.has_attendant !== null && fair.has_attendant !== undefined && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-teal-100 text-primary-dark">
                  {fair.has_attendant ? '👥 Attended' : '🔄 Self-Serve'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Payment Methods */}
        {fair.payment_types && fair.payment_types.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-primary-dark mb-4">Payment Methods Accepted</h2>
            <div className="flex flex-wrap gap-3">
              {fair.payment_types
                .sort((a, b) => a.display_order - b.display_order)
                .map((pt) => (
                  <span
                    key={pt.id}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-teal-100 text-primary-dark"
                  >
                    {pt.icon && <span className="mr-1.5">{pt.icon}</span>}
                    {pt.display_name}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Interactive Booth Map Preview - Issue #1199, #1159, #1452 */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-primary-dark mb-2 flex items-center gap-2">
            <span>🗺️</span>
            <span>Venue Layout</span>
          </h2>
          <p className="text-gray-600 mb-4">
            {isSeededFair
              ? "Preview the venue layout. Vendor features will be available once a fair manager is assigned."
              : "Explore the interactive booth map to see available spaces, amenities, and venue layout."}
          </p>

          {/* Issue #1452: Show info message for seeded fairs */}
          {/* Issue #1453: Add Apply Now button for seeded fairs */}
          {isSeededFair && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">ℹ️</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 mb-1">Manager Assignment Pending</h3>
                  <p className="text-sm text-amber-800 mb-3">
                    This fair is awaiting manager assignment. Vendor booth reservations, inquiries, and other features
                    will become available once a fair manager completes onboarding.
                  </p>
                  <button
                    onClick={() => setShowApplyNowModal(true)}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition font-medium text-sm"
                  >
                    Apply to Manage This Fair
                  </button>
                </div>
              </div>
            </div>
          )}

          <InteractiveBoothMapWrapper fairId={fair.id} />
          {!isSeededFair && slotAvailability && slotAvailability.available_slots > 0 && (
            <div className="mt-4 p-4 bg-teal-50 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <span className="text-sm text-gray-700">Available Booths: </span>
                  <span className="font-bold text-primary-dark">
                    {slotAvailability.available_slots} of {slotAvailability.total_slots}
                  </span>
                  <span className="ml-3 text-sm text-gray-700">Starting at: </span>
                  <span className="font-bold text-primary-dark">{formatCurrency(slotAvailability.min_price)}/day</span>
                </div>
                <Link
                  to={`/fairs/${fair.id}/slots`}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition font-medium text-sm"
                >
                  View Available Booths
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Vendor-Specific Features - Issue #1452: Hide for seeded fairs */}
        {!isSeededFair && currentUserId && userVendors.length > 0 && (
          <>
            {/* Smart Booth Recommendations (Issue #732) */}
            {slotAvailability && slotAvailability.available_slots > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-primary-dark mb-2 flex items-center gap-2">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                  <span>Smart Booth Recommendations</span>
                </h2>
                <p className="text-gray-600 mb-4">
                  Get personalized booth recommendations based on your saved preferences.
                </p>
                <button
                  onClick={() => setShowSmartRecommendations(!showSmartRecommendations)}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition font-medium w-full sm:w-auto flex items-center justify-center gap-2"
                  aria-expanded={showSmartRecommendations}
                  aria-controls="smart-recommendations-content"
                >
                  <Sparkles className="h-5 w-5" />
                  {showSmartRecommendations ? 'Hide Recommendations' : 'See Recommended Booths'}
                </button>
                {showSmartRecommendations && (
                  <div
                    id="smart-recommendations-content"
                    className="mt-4"
                    role="region"
                    aria-label="Smart booth recommendations"
                  >
                    <SmartBoothSelector
                      fairId={fair.id}
                      vendorId={userVendors[0].id}
                      onSelectBooth={(slotId) => {
                        navigate(`/slots/${slotId}/reserve`);
                      }}
                      onViewAllSlots={() => setShowSlotModal(true)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Contact Fair Manager (Issue #377) */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-primary-dark mb-2 flex items-center gap-2">
                <MessageSquare className="h-6 w-6 text-secondary" />
                <span>Have Questions?</span>
              </h2>
              <p className="text-gray-600 mb-4">
                Need information about booth reservations, pricing, or event logistics?
              </p>
              <button
                onClick={() => setShowInquiryModal(true)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition font-medium w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <MessageSquare className="h-5 w-5" />
                Contact Fair Manager
              </button>
            </div>
          </>
        )}
      </div>

      {/* Reviews Section - Issue #1507: Hidden by feature flag */}
      {showCustomerReviews && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ReviewList fairId={fair.id} currentUserId={currentUserId} />
        </div>
      )}

      {/* Slot Selection Modal (Issue #348) */}
      {showSlotModal && fair && (
        <SlotSelectionModal
          fairId={fair.id}
          fairName={fair.name}
          isOpen={showSlotModal}
          onClose={() => setShowSlotModal(false)}
        />
      )}

      {/* Fair Inquiry Modal (Issue #377) */}
      {fair && (
        <FairInquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          fairId={fair.id}
          fairName={fair.name}
          vendors={userVendors}
        />
      )}

      {/* Apply Now Modal (Issue #1453) */}
      {fair && showApplyNowModal && (
        <ApplyNowFlow
          fair={fair}
          onClose={() => setShowApplyNowModal(false)}
        />
      )}
    </>
  );
}